import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import TextField from "../../components/customMui/TextField";
import Button from "../../components/customMui/Button";
import LoadingButton from "../../components/customMui/LoadingButton";
import Alert from "../../components/customMui/Alert";
import standardBizApiClient from "../../util/BizApi/standardBizApiClient";
import pagePath from "../../util/constants/pagePath";
import Check from '@mui/icons-material/Done';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import "./resetPassword.css";

const passwordRequirementMessage = "Password must be at least 8 characters long,1 uppercase letter, 1 lowercase letter, 1 number, 1 special character.";

const ResetPassword = () => {
    const [submitLoading, setSubmitLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showSuccess, setShowSuccess] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [failedAttempts, setFailedAttempts] = useState(0);
    const [uppercaseValidation, setUppercaseValidation] = useState(false);
    const [lowercaseValidation, setLowercaseValidation] = useState(false);
    const [digitValidation, setDigitValidation] = useState(false);
    const [specialCharValidation, setSpecialCharValidation] = useState(false);
    const [lengthValidation, setLengthValidation] = useState(false);
    const [passwordValidated, setPasswordValidated] = useState(false);
    const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
    const params = useParams();
    const passwordMismatchError = errorMessage === "Passwords do not match";
    const passwordRequirements = [
        { label: "8 char long", valid: lengthValidation },
        { label: "1 upp case", valid: uppercaseValidation },
        { label: "1 low case", valid: lowercaseValidation },
        { label: "1 num", valid: digitValidation },
        { label: "1 sp char", valid: specialCharValidation },
    ];

    useEffect(() => {
        const storedAttempts = localStorage.getItem("resetPasswordFailedAttempts");
        const storedLastAttempt = localStorage.getItem("resetPasswordLastAttemptTime");

        if (storedAttempts) {
            const attempts = parseInt(storedAttempts, 10);
            if (storedLastAttempt && Date.now() - parseInt(storedLastAttempt, 10) > 660 * 1000) {
                localStorage.removeItem("resetPasswordFailedAttempts");
                localStorage.removeItem("resetPasswordLastAttemptTime");
                setFailedAttempts(0);
            } else {
                setFailedAttempts(attempts);
            }
        }

        const storedExpiry = localStorage.getItem("resetPasswordLockoutExpiry");
        if (storedExpiry) {
            const remaining = Math.ceil((parseInt(storedExpiry, 10) - Date.now()) / 1000);
            if (remaining > 0) {
                setCountdown(remaining);
            } else {
                localStorage.removeItem("resetPasswordLockoutExpiry");
            }
        }

        const handleStorageChange = (e) => {
            if (e.key === "resetPasswordLockoutExpiry") {
                if (e.newValue) {
                    const remaining = Math.ceil((parseInt(e.newValue, 10) - Date.now()) / 1000);
                    if (remaining > 0) setCountdown(remaining);
                } else {
                    setCountdown(0);
                    if (errorMessage) {
                        setErrorMessage(null);
                    }
                }
            }
        };

        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, [errorMessage]);

    useEffect(() => {
        let timer;
        if (countdown > 0) {
            setErrorMessage(<span>Too many attempts. Please wait <span style={{ display: "inline-block", width: "42px", fontVariantNumeric: "tabular-nums", textAlign: "center" }}>{formatTimer(countdown)}</span> and try again.</span>);
            timer = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        if (errorMessage) {
                            setErrorMessage(null);
                        }
                        localStorage.removeItem("resetPasswordLockoutExpiry");
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [countdown, errorMessage]);

    const formatTimer = (totalSeconds) => {
        const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
        const seconds = String(totalSeconds % 60).padStart(2, "0");
        return `${minutes}:${seconds}`;
    };

    const isStrongPassword = (value) => (
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/.test(value)
    );

    const handlePasswordValidation = (value) => {
        const oneUppercaseValidation = /^(?=.*[A-Z])/.test(value);
        const oneLowercaseValidation = /^(?=.*[a-z])/.test(value);
        const oneDigitValidation = /^(?=.*[0-9])/.test(value);
        const oneSpecialCharValidation = /^(?=.*[^a-zA-Z0-9\s])/.test(value);
        const minLengthValidation = value.length > 7;

        setUppercaseValidation(oneUppercaseValidation);
        setLowercaseValidation(oneLowercaseValidation);
        setDigitValidation(oneDigitValidation);
        setSpecialCharValidation(oneSpecialCharValidation);
        setLengthValidation(minLengthValidation);
        setPasswordValidated(
            oneUppercaseValidation &&
            oneLowercaseValidation &&
            oneDigitValidation &&
            oneSpecialCharValidation &&
            minLengthValidation
        );
        setPassword(value);
    };

    const handlePasswordFocus = () => {
        setShowPasswordRequirements(true);
    };

    const getLimitSecondsFromError = (errorObj) => {
        const kvdetail = errorObj?.kvdetail || [];
        const limitItem = kvdetail.find((item) => item.key === "wait_seconds" || item.key === "limit_seconds_remaining");
        return Number(limitItem?.value || 0);
    };

    const startLockout = (seconds) => {
        setCountdown(seconds);
        localStorage.setItem("resetPasswordLockoutExpiry", (Date.now() + seconds * 1000).toString());
    };

    const handleResetPassword = async e => {
        e.preventDefault();

        if (countdown > 0) {
            setErrorMessage(<span>Too many attempts. Please wait <span style={{ display: "inline-block", width: "42px", fontVariantNumeric: "tabular-nums", textAlign: "center" }}>{formatTimer(countdown)}</span> and try again.</span>);
            return;
        }

        const newFailedAttempts = failedAttempts + 1;
        setFailedAttempts(newFailedAttempts);
        localStorage.setItem("resetPasswordFailedAttempts", newFailedAttempts.toString());
        localStorage.setItem("resetPasswordLastAttemptTime", Date.now().toString());

        if (newFailedAttempts === 6) {
            startLockout(60);
            return;
        } else if (newFailedAttempts >= 9) {
            startLockout(600);
            return;
        }

        setErrorMessage(null)
        if (password !== confirmPassword) {
            setErrorMessage("Passwords do not match")
            return;
        }

        if (!isStrongPassword(password)) {
            setErrorMessage(passwordRequirementMessage);
            return;
        }

        setSubmitLoading(true);
        const token = params?.token;
        const resetPasswordRes = await standardBizApiClient.account_reset_password(password, token);
        if (resetPasswordRes?.is_error) {
            if (resetPasswordRes?.context_code === "EXPIRED") {
                setErrorMessage(<div>The reset password link has expired. Please generate a new link <Link to={pagePath.reset_password_initiate}>here</Link></div>);
                setSubmitLoading(false);
                return;
            }

            if (resetPasswordRes?.context_code === "WEAK_PASSWORD") {
                setErrorMessage(resetPasswordRes?.message || passwordRequirementMessage);
                setSubmitLoading(false);
                return;
            }

            if (resetPasswordRes?.context_code === "SAME_PASSWORD") {
                setErrorMessage("This password is already used please try another password");
                setSubmitLoading(false);
                return;
            }

            const isRateLimited = resetPasswordRes?.code === "RATE_LIMIT" || resetPasswordRes?.context_code === "RESET_PASSWORD_RATE_LIMIT";
            if (isRateLimited && (newFailedAttempts === 6 || newFailedAttempts >= 9)) {
                const backendSeconds = getLimitSecondsFromError(resetPasswordRes);
                const policySeconds = newFailedAttempts >= 9 ? 600 : 60;
                const remainingSeconds = Math.max(backendSeconds, policySeconds);
                startLockout(remainingSeconds);
            } else {
                setErrorMessage("Error while resetting password, please try again soon.");
            }

            setSubmitLoading(false);
            return;
        }

        setFailedAttempts(0);
        localStorage.removeItem("resetPasswordFailedAttempts");
        setCountdown(0);
        localStorage.removeItem("resetPasswordLockoutExpiry");
        setShowSuccess(true);
        setSubmitLoading(false);
    }

    if (showSuccess) {
        return (
            <div className="reset-password reset-password-success">
                <h2 className="font-header-light">Password Reset Successfully!</h2>
                <p>You can now login using your new password.</p>
                <Link to={pagePath.login}>
                    <Button>Continue to Login</Button>
                </Link>
            </div>
        )
    }

    return (
        <div className="reset-password">
            <h2 className="font-header-light">Reset Password</h2>
            <form className="reset-password-form" onSubmit={handleResetPassword}>
                {errorMessage && !passwordMismatchError ? (<Alert severity="error">{errorMessage}</Alert>) : null}
                <TextField
                    required
                    disabled={submitLoading || countdown > 0}
                    value={password}
                    onChange={e => handlePasswordValidation(e.target.value)}
                    onFocus={handlePasswordFocus}
                    id="reset-password-password"
                    type="password"
                    label="New Password"
                />
                {showPasswordRequirements ? (
                    <div className="password-requirements">
                        <ul className="password-requirements-list">
                            {passwordRequirements.map(({ label, valid }) => (
                                <li key={label} className={`password-req-item pass-validation-${valid}`}>
                                    {valid ? <Check fontSize="small" /> : <HighlightOffIcon fontSize="small" />}
                                    <span>{label}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : null}
                <TextField
                    required
                    disabled={submitLoading || countdown > 0 || !passwordValidated}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    id="reset-password-confirm-password"
                    type="password"
                    label="Retype New Password"
                />
                {passwordMismatchError ? (<Alert severity="error">{errorMessage}</Alert>) : null}
                <LoadingButton customProps={{ type: "submit", loading: submitLoading, disabled: countdown > 0 }}>Reset Password</LoadingButton>
            </form>
        </div>
    )
}

export default ResetPassword;